// OAI Cart System - Namespaced clone of cart.js logic
// Depends on: window.oaiRoutes, window.oaiCartStrings, window.oaiQuickOrderListStrings, window.oaiVariantStrings
// Uses OAI pubsub: oaiPublish / oaiSubscribe with event names in OAI_PUB_SUB_EVENTS (must be defined elsewhere)

(function () {
  // Fallback utilities (debounce, fetchConfig, performance wrappers) -----------------
  const DEBOUNCE_TIMER =
    typeof ON_CHANGE_DEBOUNCE_TIMER !== "undefined"
      ? ON_CHANGE_DEBOUNCE_TIMER
      : 300;
  function oaiDebounce(fn, wait = DEBOUNCE_TIMER) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
  function oaiFetchConfig() {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }
  const OaiCartPerformance = {
    measureFromEvent(label, evt) {
      if (window.performance && performance.mark) {
        performance.mark(label + "-start");
        performance.mark(label + "-end");
        performance.measure(label, label + "-start", label + "-end");
      }
    },
    measure(label, fn) {
      if (window.performance && performance.mark) {
        performance.mark(label + "-start");
        fn();
        performance.mark(label + "-end");
        performance.measure(label, label + "-start", label + "-end");
      } else {
        fn();
      }
    },
  };
  // Focus helpers fallbacks ---------------------------------------------
  function oaiTrapFocus(container, element) {
    if (typeof trapFocus === "function") {
      trapFocus(container, element);
    } else {
      element && element.focus && element.focus();
    }
  }
  function oaiRemoveTrapFocus(active) {
    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(active);
    }
  }

  // Simple pubsub event names fallback -----------------------------------
  const EVENTS =
    typeof OAI_PUB_SUB_EVENTS !== "undefined"
      ? OAI_PUB_SUB_EVENTS
      : { cartUpdate: "oai:cart-update" };

  // CartRemoveButton ------------------------------------------------------
  if (!customElements.get("oai-cart-remove-button")) {
    class OaiCartRemoveButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener("click", (e) => {
          e.preventDefault();
          const cartItems =
            this.closest("oai-cart-items") ||
            this.closest("oai-cart-drawer-items");
          cartItems && cartItems.updateQuantity(this.dataset.index, 0, e);
        });
      }
    }
    customElements.define("oai-cart-remove-button", OaiCartRemoveButton);
  }

  // OaiCartItems ----------------------------------------------------------
  class OaiCartItems extends HTMLElement {
    constructor() {
      super();
      this.lineItemStatusElement =
        document.getElementById("OaiShoppingCartLineItemStatus") ||
        document.getElementById("OaiCartDrawer-LineItemStatus");
      const debounced = oaiDebounce((evt) => this.onChange(evt));
      this.addEventListener("change", debounced);
    }
    connectedCallback() {
      this.unsubscribe = oaiSubscribe(EVENTS.cartUpdate, (payload) => {
        if (payload && payload.source === "oai-cart-items") return;
        return this.onCartUpdate();
      });
    }
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    resetQuantityInput(id) {
      const input = this.querySelector(`#OaiQuantity-${id}`);
      if (input) {
        input.value = input.getAttribute("value");
        this.isEnterPressed = false;
      }
    }
    setValidity(event, index, message) {
      event.target.setCustomValidity(message);
      event.target.reportValidity();
      this.resetQuantityInput(index);
      event.target.select();
    }
    validateQuantity(event) {
      const val = parseInt(event.target.value);
      const idx = event.target.dataset.index;
      let msg = "";
      if (val < event.target.dataset.min) {
        msg = window.oaiQuickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        );
      } else if (val > parseInt(event.target.max)) {
        msg = window.oaiQuickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        );
      } else if (val % parseInt(event.target.step) !== 0) {
        msg = window.oaiQuickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        );
      }
      if (msg) {
        this.setValidity(event, idx, msg);
      } else {
        event.target.setCustomValidity("");
        event.target.reportValidity();
        this.updateQuantity(
          idx,
          val,
          event,
          document.activeElement.getAttribute("name"),
          event.target.dataset.quantityVariantId
        );
      }
    }
    onChange(e) {
      this.validateQuantity(e);
    }
    onCartUpdate() {
      if (this.tagName === "OAI-CART-DRAWER-ITEMS") {
        return fetch(`${window.oaiRoutes.cart_url}?section_id=oai-cart-drawer`)
          .then((r) => r.text())
          .then((txt) => {
            const html = new DOMParser().parseFromString(txt, "text/html");
            ["oai-cart-drawer-items", ".oai-cart-drawer__footer"].forEach(
              (sel) => {
                const tgt = document.querySelector(sel);
                const src = html.querySelector(sel);
                if (tgt && src) tgt.replaceWith(src);
              }
            );
          })
          .catch(console.error);
      } else {
        return fetch(`${window.oaiRoutes.cart_url}?section_id=main-cart-items`)
          .then((r) => r.text())
          .then((txt) => {
            const html = new DOMParser().parseFromString(txt, "text/html");
            const source = html.querySelector("oai-cart-items");
            if (source) this.innerHTML = source.innerHTML;
          })
          .catch(console.error);
      }
    }
    getSectionsToRender() {
      const mainItems = document.getElementById("oai-main-cart-items");
      const footer = document.getElementById("oai-main-cart-footer");
      return [
        {
          id: "oai-main-cart-items",
          section: mainItems ? mainItems.dataset.id : "main-cart-items",
          selector: ".js-contents",
        },
        {
          id: "oai-cart-icon-bubble",
          section: "oai-cart-icon-bubble",
          selector: ".shopify-section",
        },
        {
          id: "oai-cart-live-region-text",
          section: "cart-live-region-text",
          selector: ".shopify-section",
        },
        {
          id: "oai-main-cart-footer",
          section: footer ? footer.dataset.id : "main-cart-footer",
          selector: ".js-contents",
        },
      ];
    }
    updateQuantity(line, quantity, event, name, variantId) {
      this.enableLoading(line);
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });
      const eventTarget =
        event.currentTarget instanceof HTMLElement &&
        event.currentTarget.tagName === "OAI-CART-REMOVE-BUTTON"
          ? "clear"
          : "change";
      fetch(`${window.oaiRoutes.cart_change_url}`, {
        ...oaiFetchConfig(),
        body,
      })
        .then((r) => r.text())
        .then((state) => {
          const parsed = JSON.parse(state);
          OaiCartPerformance.measure(
            `${eventTarget}:paint-updated-sections`,
            () => {
              const qtyEl =
                document.getElementById(`OaiQuantity-${line}`) ||
                document.getElementById(`OaiDrawer-quantity-${line}`);
              const items = document.querySelectorAll(".oai-cart-item");
              if (parsed.errors) {
                if (qtyEl) qtyEl.value = qtyEl.getAttribute("value");
                this.updateLiveRegions(line, parsed.errors);
                return;
              }
              this.classList.toggle("is-empty", parsed.item_count === 0);
              const drawerWrapper = document.querySelector("oai-cart-drawer");
              const cartFooter = document.getElementById(
                "oai-main-cart-footer"
              );
              if (cartFooter)
                cartFooter.classList.toggle(
                  "is-empty",
                  parsed.item_count === 0
                );
              if (drawerWrapper)
                drawerWrapper.classList.toggle(
                  "is-empty",
                  parsed.item_count === 0
                );
              this.getSectionsToRender().forEach((section) => {
                const elToReplace =
                  document
                    .getElementById(section.id)
                    ?.querySelector(section.selector) ||
                  document.getElementById(section.id);
                if (elToReplace) {
                  elToReplace.innerHTML = this.getSectionInnerHTML(
                    parsed.sections[section.section],
                    section.selector
                  );
                }
              });
              const updatedValue = parsed.items[line - 1]
                ? parsed.items[line - 1].quantity
                : undefined;
              let message = "";
              if (
                items.length === parsed.items.length &&
                updatedValue !== (qtyEl ? parseInt(qtyEl.value) : undefined)
              ) {
                message =
                  typeof updatedValue === "undefined"
                    ? window.oaiCartStrings.error
                    : window.oaiCartStrings.quantityError.replace(
                        "[quantity]",
                        updatedValue
                      );
              }
              this.updateLiveRegions(line, message);
              const lineItem =
                document.getElementById(`OaiCartItem-${line}`) ||
                document.getElementById(`OaiCartDrawer-Item-${line}`);
              if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
                drawerWrapper
                  ? oaiTrapFocus(
                      drawerWrapper,
                      lineItem.querySelector(`[name="${name}"]`)
                    )
                  : lineItem.querySelector(`[name="${name}"]`).focus();
              } else if (parsed.item_count === 0 && drawerWrapper) {
                oaiTrapFocus(
                  drawerWrapper.querySelector(".oai-drawer__inner-empty"),
                  drawerWrapper.querySelector("a")
                );
              } else if (
                document.querySelector(".oai-cart-item") &&
                drawerWrapper
              ) {
                oaiTrapFocus(
                  drawerWrapper,
                  document.querySelector(".oai-cart-item__name")
                );
              }
            }
          );
          OaiCartPerformance.measureFromEvent(
            `${eventTarget}:user-action`,
            event
          );
          oaiPublish(EVENTS.cartUpdate, {
            source: "oai-cart-items",
            cartData: parsed,
            variantId,
          });
        })
        .catch(() => {
          this.querySelectorAll(".oai-loading-spinner").forEach((sp) =>
            sp.classList.add("hidden")
          );
          const errors =
            document.getElementById("oai-cart-errors") ||
            document.getElementById("OaiCartDrawer-CartErrors");
          if (errors) errors.textContent = window.oaiCartStrings.error;
        })
        .finally(() => {
          this.disableLoading(line);
        });
    }
    updateLiveRegions(line, message) {
      const lineErr =
        document.getElementById(`OaiLineItemError-${line}`) ||
        document.getElementById(`OaiCartDrawer-LineItemError-${line}`);
      if (lineErr)
        lineErr.querySelector(".oai-cart-item__error-text").textContent =
          message;
      if (this.lineItemStatusElement)
        this.lineItemStatusElement.setAttribute("aria-hidden", true);
      const cartStatus =
        document.getElementById("oai-cart-live-region-text") ||
        document.getElementById("OaiCartDrawer-LiveRegionText");
      if (cartStatus) {
        cartStatus.setAttribute("aria-hidden", false);
        setTimeout(() => {
          cartStatus.setAttribute("aria-hidden", true);
        }, 1000);
      }
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(selector).innerHTML;
    }
    enableLoading(line) {
      const mainItems =
        document.getElementById("oai-main-cart-items") ||
        document.getElementById("OaiCartDrawer-CartItems");
      if (mainItems) mainItems.classList.add("oai-cart__items--disabled");

      const els = [
        ...this.querySelectorAll(`#OaiCartItem-${line} .oai-loading-spinner`),
        ...this.querySelectorAll(
          `#OaiCartDrawer-Item-${line} .oai-loading-spinner`
        ),
      ];
      els.forEach((o) => o.classList.remove("hidden"));
      document.activeElement.blur();
      if (this.lineItemStatusElement)
        this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
    disableLoading(line) {
      const mainItems =
        document.getElementById("oai-main-cart-items") ||
        document.getElementById("OaiCartDrawer-CartItems");
      if (mainItems) mainItems.classList.remove("oai-cart__items--disabled");
      const els = [
        ...this.querySelectorAll(`#OaiCartItem-${line} .oai-loading-spinner`),
        ...this.querySelectorAll(
          `#OaiCartDrawer-Item-${line} .oai-loading-spinner`
        ),
      ];
      els.forEach((o) => o.classList.add("hidden"));
    }
  }
  if (!customElements.get("oai-cart-items")) {
    customElements.define("oai-cart-items", OaiCartItems);
  }

  // Cart Note -------------------------------------------------------------
  if (!customElements.get("oai-cart-note")) {
    class OaiCartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          "input",
          oaiDebounce((evt) => {
            const body = JSON.stringify({ note: evt.target.value });
            fetch(`${window.oaiRoutes.cart_update_url}`, {
              ...oaiFetchConfig(),
              body,
            }).then(() =>
              OaiCartPerformance.measureFromEvent(
                "note-update:user-action",
                evt
              )
            );
          })
        );
      }
    }
    customElements.define("oai-cart-note", OaiCartNote);
  }

  // Drawer Items subclass -------------------------------------------------
  class OaiCartDrawerItems extends OaiCartItems {
    getSectionsToRender() {
      return [
        {
          id: "OaiCartDrawer",
          section: "oai-cart-drawer",
          selector: ".oai-drawer__inner",
        },
        {
          id: "oai-cart-icon-bubble",
          section: "oai-cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }
  if (!customElements.get("oai-cart-drawer-items")) {
    customElements.define("oai-cart-drawer-items", OaiCartDrawerItems);
  }
})();
